/** Mean Earth radius in statute miles. */
export const R_EARTH_MI = 3958.7613;
/** Assumed average travel speed for ETA calc (mph). */
export const AVG_TRAVEL_MPH = 22;

const toRad = (deg) => (deg * Math.PI) / 180;

/** Robust haversine distance in miles. Returns +Infinity on bad input. */
export function haversineMiles([lat1, lon1], [lat2, lon2]) {
  if ([lat1, lon1, lat2, lon2].some((n) => typeof n !== 'number' || Number.isNaN(n))) {
    return Number.POSITIVE_INFINITY;
  }
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.asin(Math.sqrt(a));
  return R_EARTH_MI * c;
}

/** Convert miles to whole minutes using AVG_TRAVEL_MPH; min 3 mins; null on bad input. */
export function minutesFromMiles(distanceMiles) {
  if (!Number.isFinite(distanceMiles)) return null;
  return Math.max(3, Math.round((distanceMiles / AVG_TRAVEL_MPH) * 60));
}

/** Format distance in miles for display (e.g., "0.5 mi" or "2.3 mi"). */
export function formatMiles(miles) {
  if (!Number.isFinite(miles)) return null;
  if (miles < 0.1) {
    const feet = Math.round(miles * 5280);
    return `${feet} ft`;
  }
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

/**
 * Generate Google Maps directions URL for a destination.
 * @param {number} lat - Destination latitude
 * @param {number} lng - Destination longitude
 * @param {string} [label] - Optional place name
 * @returns {string} Google Maps directions URL
 */
export function getDirectionsUrl(lat, lng, label) {
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const destination = hasCoords ? `${lat},${lng}` : encodeURIComponent(label ?? '');
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
}
